import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

import { MtnaLogger } from '@mtna/lib-ui';

import { ExternalRdsAppConfig } from '../models/index';
import { AnalyticsUtil } from '../utils/analytics-util';
import { HotjarUtil } from '../utils/hotjar-util';

export interface RdsApiConfig {
  configPath: string;
}

export const RDS_API_CONFIG = new InjectionToken<RdsApiConfig>('rdsApiConfig');

/**
 * Service that acts as a central hub for all RDS interactions.
 * All other RDS services rely on this services for the api url, selected catalog id, and selected data product id.
 *
 * This service can be used in one of two ways.
 * 1. Use the optional RDS_API_CONFIG to inject a path for a json config file. Be sure to load the config file in the `APP_INITIALIZER`.
 * 2. Don't provide the RDS_API_CONFIG and manually set the api url, and optionally default catalog and data product IDs
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Injectable({ providedIn: 'root' })
export class RdsApiService {
  /** API url for the RDS instance */
  apiUrl: string;
  /** Selected Catalog ID */
  catalogId: string;
  /** Seleted Data Product ID */
  dataProductId: string;

  /** Object read from config JSON file */
  private config: ExternalRdsAppConfig = null;
  /** Path to the config JSON file */
  private configPath: string;

  /**
   * Creates an instance of RdsApiService.
   * @param http HttpClient
   * @param rdsApiConfig optional configuration that tells where to load the config file
   */
  constructor(protected http: HttpClient, @Optional() @Inject(RDS_API_CONFIG) rdsApiConfig: RdsApiConfig, protected logger: MtnaLogger) {
    this.configPath = !!rdsApiConfig ? rdsApiConfig.configPath : null;
    this.logger.debug('[RdsApiService] constructor - config', rdsApiConfig);
  }

  /**
   * Called by all other services for http calls requiring the api url
   */
  getApiUrl(): string {
    if (!this.apiUrl) {
      this.logger.warn(`[RdsApiService] getApiUrl - API URL has not been set.`);
    }
    return this.apiUrl;
  }

  setApiUrl(url: string) {
    this.apiUrl = url;
  }

  /**
   * Use to get the data found in the second file (config file)
   */
  getConfig<K extends keyof ExternalRdsAppConfig>(key: K) {
    if (!this.config) {
      this.logger.error(`[RdsApiService] getConfig - config has not been loaded.`);
    }
    return this.config[key];
  }

  /**
   * Returns the id of the selected catalog. Throws error if
   * the id has not been set.
   */
  getCatalogId(): string {
    if (!this.catalogId) {
      this.logger.warn(`[RdsApiService] getCatalogId - Catalog ID has not been set.`);
    }
    return this.catalogId;
  }

  /**
   * Checks if the catalog id has been set.
   */
  isCatalogIdSet(): boolean {
    return !!this.catalogId;
  }

  /**
   * Sets the catalogId using the selected catalog.
   */
  setCatalogId(id: string): void {
    this.catalogId = id;
  }

  /**
   * Returns the id of the selected data product. Throws error if
   * the id has not been set.
   */
  getDataProductId(): string {
    if (!this.dataProductId) {
      this.logger.warn(`[RdsApiService] getApiUrl - Data Product ID has not been set.`);
    }
    return this.dataProductId;
  }

  /**
   * Checks if the data product id has been set.
   */
  isDataProductIdSet(): boolean {
    return !!this.dataProductId;
  }

  /**
   * Set the dataProductId using the selected data product.
   */
  setDataProductId(id: string): void {
    this.dataProductId = id;
  }

  /**
   * Load a config file located at the path provided in `RDS_API_CONFIG`.
   * This should be done in the angular `APP_INITIALIZER`
   *
   * @example
   *
   * function initConfig(config: RdsApiService) {
   *    return () => config.loadConfigFile();
   * }
   *
   * // AppModule providers
   * providers: [
   *  {
   *    provide: APP_INITIALIZER,
   *    useFactory: initConfig,
   *    deps: [RdsApiService],
   *    multi: true
   *  }
   * ],
   */
  loadConfigFile(): Promise<void> {
    this.logger.debug('[RdsApiService] loadConfigFile');
    return new Promise((resolve, reject) => {
      if (!this.configPath) {
        throw new Error('[RdsApiService] loadConfigFile - RDS_API_CONFIG has not been provided, cannot load configuration from file');
      }
      this.http
        .get(this.configPath)
        .toPromise()
        .then((configResponse: ExternalRdsAppConfig) => {
          this.logger.debug(`[RdsApiService] loadConfigFile - configResponse`, configResponse);
          this.config = configResponse;
          this._initializeConfigurations();
          resolve();
        })
        .catch((err) => {
          this.logger.error(`[RdsApiService] loadConfigFile - Configuration file ${this.configPath} could not be read`);
          reject(`Could not load file '${this.configPath}': ${JSON.stringify(err)}`);
        });
    });
  }

  /**
   * Read the config file and set the api url and any defaults
   */
  private _initializeConfigurations() {
    if (!!this.config) {
      const rdsApiUrl = this.getConfig('RDS_API_URL');
      if (!!rdsApiUrl) {
        this.setApiUrl(rdsApiUrl);
        this.logger.debug('[RdsApiService] _initializeConfigurations - set api url:', this.getApiUrl());
      }
      const defaultCatalog = this.getConfig('DEFAULT_CATALOG');
      if (!!defaultCatalog) {
        this.setCatalogId(defaultCatalog);
        this.logger.debug('[RdsApiService] _initializeConfigurations - set default catalog: ', defaultCatalog);
      }
      const defaultDataProduct = this.getConfig('DEFAULT_DATA_PRODUCT');
      if (!!defaultDataProduct) {
        this.setDataProductId(defaultDataProduct);
        this.logger.debug('[RdsApiService] _initializeConfigurations - set default data product: ', defaultDataProduct);
      }
      const hotjarId = this.getConfig('hotjarId');
      if (hotjarId) {
        HotjarUtil.initialize(hotjarId);
        this.logger.debug('[RdsApiService] _initializeConfigurations - initialzed hotjar with id: ', hotjarId);
      }
      const googleId = this.getConfig('googleId');
      if (googleId) {
        AnalyticsUtil.initialize(googleId);
        this.logger.debug(`[RdsApiService] _intializeConfigurations - initialized google analytics with id: `, googleId);
      }
    }
  }
}
